import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Scale,
  Ruler,
  Target,
  Activity,
  Gauge,
  Camera,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import api from "services/api";
import useStore from "services/store";

export const Profile = () => {
  const navigate = useNavigate();
  const { user, setUser, setMetrics } = useStore();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(user || null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    switch (name) {
      case "first_name": return !value ? "First name is required" : value.length < 2 ? "First name must be at least 2 characters" : "";
      case "last_name": return value && value.length < 2 ? "Last name must be at least 2 characters" : "";
      case "phone_number": return !value ? "Phone number is required" : !/^\+?[\d\s-]{10,}$/.test(value) ? "Invalid phone number format" : "";
      case "location": return !value ? "Location is required" : value.length !== 5 ? "Location must be 5 characters" : "";
      case "current_weight": return !value ? "Current weight is required" : value < 0 ? "Weight must be positive" : "";
      case "target_weight": return !value ? "Target weight is required" : value < 0 ? "Weight must be positive" : "";
      case "height": return !value ? "Height is required" : value < 0 ? "Height must be positive" : "";
      case "body_fat": return value && (value < 0 || value > 100) ? "Body fat percentage must be between 0% and 100%" : "";
      case "goal": 
        if (!value) return "Goal is required";
        const tw = parseFloat(formData.target_weight);
        const cw = parseFloat(formData.current_weight);
        if (value === "lose_fat" && tw >= cw) return "Target weight must be less than current weight to lose fat";
        if (value === "maintain_weight" && tw !== cw) return "Target weight must equal current weight to maintain weight";
        if (value === "gain_muscle" && tw <= cw) return "Target weight must be greater than current weight to gain muscle";
        return "";
      default: return "";
    }
  };

  const activityLevels = [
    { value: "sedentary", label: "Sedentary", description: "little or no exercise" },
    { value: "light", label: "Light", description: "exercise 1-3 times/week" },
    { value: "moderate", label: "Moderate", description: "exercise 3-5 times/week" },
    { value: "active", label: "Active", description: "exercise 6-7 times/week" },
    { value: "very_active", label: "Very Active", description: "exercise & physical work" },
  ];

  const goals = [
    { value: "lose_fat", label: "Lose Fat", description: "Reduce body fat while preserving muscle mass" },
    { value: "maintain_weight", label: "Maintain Weight", description: "Keep current weight while improving body composition" },
    { value: "gain_muscle", label: "Gain Muscle", description: "Build muscle mass through progressive overload" },
  ];

  const progressRates = [
    { value: "slow", label: "Slow", description: "0.25 kg/week" },
    { value: "moderate", label: "Moderate", description: "0.5 kg/week" },
    { value: "fast", label: "Fast", description: "1 kg/week" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData((prev) => ({
          ...prev,
          profile_picture: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = [
      "first_name",
      "last_name", 
      "phone_number",
      "location",
      "current_weight",
      "target_weight",
      "height",
      "body_fat",
      "goal",
    ];

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }
    try {
      const response = await api.put("/user/profile", formData);
      if (!response.ok) {
        toast.error(response.message || "Failed to update profile");
        for (const error of response.errors) {
          toast.error(error);
        }
        return;
      }
      setUser(response.data);
      setMetrics(response.metrics);
      setIsEditing(false);
      setErrors({});
      toast.success(response.message || "Profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
    }
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete your account? This action is irreversible."
      )
    ) {
      try {
        const response = await api.remove("/user/profile");
        if (!response.ok) {
          toast.error(response.message || "Failed to delete account");
          return;
        }
        setUser(null);
        localStorage.removeItem("token");
        api.setToken("");
        toast.success(response.message || "Account deleted successfully");
        navigate("/login");
      } catch (error) {
        toast.error("Error deleting account");
      }
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
          <h1 className="text-2xl font-bold text-black">Profile Settings</h1>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors w-full sm:w-auto"
            >
              Edit Profile
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => setIsEditing(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors w-full sm:w-auto"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Picture Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-4 flex items-center text-black">
              <Camera className="mr-2 text-blue-500" size={20} /> Profile Picture
            </h2>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  src={imagePreview || formData.profile_picture}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover"
                />
                {isEditing && (
                  <label className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-2 cursor-pointer hover:bg-blue-600 transition-colors">
                    <Camera className="text-white" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </div>
              {isEditing && (
                <div className="text-sm text-gray-600">
                  <p>Click the camera icon to upload a new photo</p>
                  <p>Maximum size: 5MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-6 flex items-center text-black">
              <User className="mr-2 text-blue-500" size={20} /> Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`block w-full rounded-lg border ${
                      errors.first_name ? "border-red-500" : "border-gray-300"
                    } px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100`}
                  />
                  {errors.first_name && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AlertCircle className="text-red-500" />
                    </div>
                  )}
                </div>
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 bg-gray-100"
                />
                {errors.last_name && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <AlertCircle className="text-red-500" />
                  </div>
                )}
              </div>
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-500">{errors.last_name}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`block w-full rounded-lg border ${
                      errors.phone_number ? "border-red-500" : "border-gray-300"
                    } px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100`}
                  />
                  {errors.phone_number && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AlertCircle className="text-red-500" />
                    </div>
                  )}
                </div>
                {errors.phone_number && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.phone_number}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={true}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={
                    new Date(formData.date_of_birth).toISOString().split("T")[0]
                  }
                  onChange={handleInputChange}
                  disabled={true}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`block w-full rounded-lg border ${
                      errors.location ? "border-red-500" : "border-gray-300"
                    } px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100`}
                  />
                  {errors.location && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AlertCircle className="text-red-500" />
                    </div>
                  )}
                </div>
                {errors.location && (
                  <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                )}
              </div>
            </div>
          </div>

          {/* Physical Metrics Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-6 flex items-center text-black">
              <Scale className="mr-2 text-blue-500" size={20} /> Physical Metrics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sex
                </label>
                <input
                  type="text"
                  name="sex"
                  value={formData.sex}
                  onChange={handleInputChange}
                  disabled={true}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Weight (kg)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="current_weight"
                    value={formData.current_weight}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    step="0.1"
                    className={`block w-full rounded-lg border ${
                      errors.current_weight
                        ? "border-red-500"
                        : "border-gray-300"
                    } px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100`}
                  />
                  {errors.current_weight && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AlertCircle className="text-red-500" />
                    </div>
                  )}
                </div>
                {errors.current_weight && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.current_weight}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Weight (kg)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="target_weight"
                    value={formData.target_weight}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    step="0.1"
                    className={`block w-full rounded-lg border ${
                      errors.target_weight
                        ? "border-red-500"
                        : "border-gray-300"
                    } px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100`}
                  />
                  {errors.target_weight && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AlertCircle className="text-red-500" />
                    </div>
                  )}
                </div>
                {errors.target_weight && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.target_weight}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Height (cm)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`block w-full rounded-lg border ${
                      errors.height ? "border-red-500" : "border-gray-300"
                    } px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100`}
                  />
                  {errors.height && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AlertCircle className="text-red-500" />
                    </div>
                  )}
                </div>
                {errors.height && (
                  <p className="mt-1 text-sm text-red-500">{errors.height}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Body Fat %
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="body_fat"
                    value={formData.body_fat}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    step="0.1"
                    className={`block w-full rounded-lg border ${
                      errors.body_fat ? "border-red-500" : "border-gray-300"
                    } px-4 py-2.5 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100`}
                  />
                  {errors.body_fat && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AlertCircle className="text-red-500" />
                    </div>
                  )}
                </div>
                {errors.body_fat && (
                  <p className="mt-1 text-sm text-red-500">{errors.body_fat}</p>
                )}
              </div>
            </div>
          </div>

          {/* Goals Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-6 flex items-center text-black">
              <Target className="mr-2 text-blue-500" size={20} /> Goals
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {goals.map((goal) => (
                <div
                  key={goal.value}
                  className={`p-4 rounded-lg border ${
                    formData.goal === goal.value
                      ? "border-gray-500 bg-gray-50"
                      : errors.goal
                      ? "border-red-500"
                      : "border-gray-200"
                  } ${isEditing ? "cursor-pointer hover:border-gray-300" : ""}`}
                  onClick={isEditing ? () => handleInputChange({target: {name: "goal", value: goal.value}}) : undefined}
                >
                  <h3 className="font-medium text-gray-900">{goal.label}</h3>
                  <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                </div>
              ))}
              {errors.goal && (
                <p className="text-sm text-red-500">{errors.goal}</p>
              )}
            </div>
          </div>

          {/* Activity Level Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-6 flex items-center text-black">
              <Activity className="mr-2 text-blue-500" size={20} /> Activity Level
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {activityLevels.map((level) => (
                <div
                  key={level.value}
                  className={`p-4 rounded-lg border ${
                    formData.activity_level === level.value
                      ? "border-gray-500 bg-gray-50"
                      : "border-gray-200"
                  } ${isEditing ? "cursor-pointer hover:border-gray-300" : ""}`}
                  onClick={isEditing ? () => handleInputChange({target: {name: "activity_level", value: level.value}}) : undefined}
                >
                  <h3 className="font-medium text-gray-900">{level.label}</h3>
                  <p className="text-sm text-gray-500 mt-1">{level.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Rate of Progress Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-6 flex items-center text-black">
              <Gauge className="mr-2 text-blue-500" size={20} /> Rate of Progress
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {progressRates.map((rate) => (
                <div
                  key={rate.value}
                  className={`p-4 rounded-lg border ${
                    formData.rate_of_progress === rate.value
                      ? "border-gray-500 bg-gray-50"
                      : "border-gray-200"
                  } ${isEditing ? "cursor-pointer hover:border-gray-300" : ""}`}
                  onClick={isEditing ? () => handleInputChange({target: {name: "rate_of_progress", value: rate.value}}) : undefined}
                >
                  <h3 className="font-medium text-gray-900">{rate.label}</h3>
                  <p className="text-sm text-gray-500 mt-1">{rate.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form Buttons */}
          {isEditing && (
            <div className="flex flex-col sm:flex-row justify-end gap-4">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 transition-colors"
              >
                Save Changes
              </button>
            </div>
          )}
        </form>

        {/* Delete Account Section */}
        <div className="mt-8">
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-black mb-4 flex items-center">
              <AlertCircle className="mr-2 text-red-500" size={20} /> Danger Zone
            </h2>
            <p className="text-black mb-4">
              Once you delete your account, there is no going back. Please be
              certain.
            </p>
            <button
              onClick={handleDeleteAccount}
              className="bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center"
            >
              <AlertCircle className="mr-2" size={16} /> Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
